import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';
import {NodeProps} from 'reactflow';

import {PipelineStep} from 'pipelines/PipelineTypes';
import {getExpectedStepDataKind} from 'pipelines/StepDataUtils';
import {getIconForStepDataKind} from 'pipelines/StepIconUtils';
import {ellipsisText} from 'styles/Utilities';
import * as Colors from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import BenchlingNode from 'ui-library/reactflow/nodes/BenchlingNode/BenchlingNode';
import {NODE_SECTION_ROW_STYLES} from 'ui-library/reactflow/nodes/BenchlingNode/BenchlingNodeDesignConstants';

import {AnalysisFlowchartAction} from './actions/AnalysisFlowchartActionTypes';
import {AnalysisGraphNodeData} from './analysis-graph/AnalysisGraphTypes';
import {useAnalysisFlowchartContext} from './AnalysisFlowchartContext';
import StepDataHandle, {StepDataHandleProps} from './handles/StepDataHandle';
import StepMenu from './menus/StepMenu';
import getStepNodeContents from './node-contents/getStepNodeContents';
import StepPopover from './popovers/StepPopover';

/**
 * Custom row renderer, for `BenchlingNode`.
 *
 * Individual input or output step datas are listed within the node, if there are multiple input or output
 * step datas. Otherwise, handles are rendered beside the header, by `HeaderHandles`.
 */
function StepDataRow({step, stepData}: StepDataHandleProps): JSX.Element {
  return (
    <span
      className={css(
        NODE_SECTION_ROW_STYLES.sectionRow,
        NODE_SECTION_ROW_STYLES.rowPrimaryColumn,
        styles.stepDataRow
      )}
    >
      <BenchlingIcon
        className={css(styles.stepDataRowIcon)}
        icon={getIconForStepDataKind(getExpectedStepDataKind(step, stepData))}
      />
      <span className={css(styles.stepDataRowText)}>{stepData.name}</span>
      <StepDataHandle step={step} stepData={stepData} />
    </span>
  );
}

function HeaderHandles({step}: {step: PipelineStep}): JSX.Element {
  return (
    <div className={css(styles.headerHandle)}>
      {step.inputStepDatas.length === 1 && <StepDataHandle step={step} stepData={step.inputStepDatas[0]} />}
      {step.outputStepDatas.length === 1 && <StepDataHandle step={step} stepData={step.outputStepDatas[0]} />}
    </div>
  );
}

export default function AnalysisFlowchartNode({
  data: {step},
  ...props
}: NodeProps<AnalysisGraphNodeData>): JSX.Element {
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const {mode, getActionInfo} = useAnalysisFlowchartContext();

  return (
    <>
      <HeaderHandles step={step} />
      <StepPopover step={step} disabled={isMenuOpen}>
        <StepMenu step={step} isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen}>
          <div
            className={css(styles.node)}
            onClick={() => {
              getActionInfo(AnalysisFlowchartAction.OPEN_STEP_CONFIGURATION, step.id).doAction?.(step.id);
            }}
            onContextMenu={(e) => {
              setIsMenuOpen(true);
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <BenchlingNode
              {...props}
              data={{
                ...getStepNodeContents(step, mode),
                hideSectionSummaries: true,
                /**
                 * If there are multiple input or output step datas, list the step datas individually, with a
                 * dedicated handle (connection point) beside each step data.
                 *
                 * Otherwise, `<HeaderHandles>` will show the individual input or output handles.
                 */
                rowRenderer: StepDataRow,
                sections: [
                  {
                    sectionName: 'Inputs',
                    sectionRows: step.inputStepDatas.map((stepData) => ({step, stepData})),
                    hideSection: step.inputStepDatas.length < 2,
                  },
                  {
                    sectionName: 'Outputs',
                    sectionRows: step.outputStepDatas.map((stepData) => ({step, stepData})),
                    hideSection: step.outputStepDatas.length < 2,
                  },
                ],
              }}
            />
          </div>
        </StepMenu>
      </StepPopover>
    </>
  );
}

/** @package Export for testing */
export const styles = StyleSheet.create({
  headerHandle: {
    // Ensure handles are positioned relative to this `div`, and not the node as a whole
    position: 'relative',
    top: 20,
  },
  node: {
    wordBreak: 'break-word', // `Float` applies `break-all`, which breaks in the middle of words
  },
  stepDataRow: {
    justifyContent: 'flex-start', // Override `flex-start` from `BenchlingNode`
    position: 'relative', // Position handles relative to the row
  },
  stepDataRowIcon: {
    color: Colors.GRAY_DARK,
    marginRight: 4,
  },
  stepDataRowText: {
    ...ellipsisText,
  },
});
